<template>
  <section class="page-shell account-page">
    <div class="page-heading">
      <div>
        <h1>Conta</h1>
        <p>Gerencie dados do escritório, plano, cobrança, cartão e histórico financeiro.</p>
      </div>
      <v-btn outlined color="primary" :loading="loading" @click="load">Atualizar</v-btn>
    </div>

    <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>
    <v-alert v-if="success" type="success" dense text>{{ success }}</v-alert>

    <div class="account-settings">
      <aside class="account-settings-nav">
        <a
          v-for="item in sections"
          :key="item.id"
          :href="`#${item.id}`"
          :class="{ active: activeSection === item.id }"
          @click.prevent="activeSection = item.id"
        >
          <v-icon small>{{ item.icon }}</v-icon>
          {{ item.label }}
        </a>
      </aside>

      <main class="account-settings-content">
        <section v-show="activeSection === 'geral'" id="geral" class="account-section">
          <div class="account-section-title">
            <h2>Geral</h2>
            <span>Dados principais do workspace</span>
          </div>
          <div class="account-form-grid">
            <v-text-field v-model="accountForm.office_name" label="Nome do escritório" outlined dense hide-details />
            <v-text-field v-model="accountForm.primary_email" label="E-mail principal" outlined dense hide-details />
          </div>
          <div class="account-row">
            <div>
              <strong>Status da conta</strong>
              <p>{{ statusLabel(subscription.status) }}</p>
            </div>
            <v-chip small :color="statusColor(subscription.status)" text-color="white">{{ statusLabel(subscription.status) }}</v-chip>
          </div>
          <v-btn color="primary" :disabled="!accountFormChanged">Salvar alterações</v-btn>
        </section>

        <section v-show="activeSection === 'plano'" id="plano" class="account-section">
          <div class="account-section-title">
            <h2>Plano</h2>
            <span>Selecione o pacote mensal da empresa</span>
          </div>
          <div class="current-plan-row">
            <div>
              <strong>{{ planName }}</strong>
              <p>Renovação ou trial até {{ formatDate(subscription.current_period_end || subscription.trial_ends_at) }}</p>
            </div>
            <v-btn outlined color="primary" @click="selectPlan(currentPlanSlug === 'trial' ? 'starter' : currentPlanSlug)">
              Alterar plano
            </v-btn>
          </div>
          <div class="plan-choice-grid">
            <article v-for="plan in plans" :key="plan.slug" :class="{ selected: currentPlanSlug === plan.slug }">
              <span>{{ plan.name }}</span>
              <strong>{{ money(plan.monthly_price) }}</strong>
              <p>{{ plan.max_users }} usuários · {{ plan.max_ai_validations }} créditos/mês</p>
              <v-btn
                small
                :class="{ 'current-plan-chip': currentPlanSlug === plan.slug }"
                :color="currentPlanSlug === plan.slug ? 'primary' : undefined"
                :outlined="currentPlanSlug !== plan.slug"
                @click="selectPlan(plan.slug)"
              >
                {{ currentPlanSlug === plan.slug ? 'Plano atual' : 'Selecionar' }}
              </v-btn>
            </article>
          </div>
        </section>

        <section v-show="activeSection === 'cobranca'" id="cobranca" class="account-section">
          <div class="account-section-title">
            <h2>Cobrança</h2>
            <span>Dados financeiros da assinatura</span>
          </div>
          <div class="account-row">
            <div>
              <strong>Perfil de cobrança</strong>
              <p>{{ subscription.asaas_customer_id ? 'Configurado' : 'Ainda não configurado' }}</p>
            </div>
            <v-chip small outlined>{{ subscription.asaas_customer_id ? 'Sincronizado' : 'Pendente' }}</v-chip>
          </div>
          <div class="account-row">
            <div>
              <strong>Assinatura</strong>
              <p>{{ subscription.asaas_subscription_id || 'Criada ao assinar um plano pago' }}</p>
            </div>
            <v-chip small outlined>{{ subscription.asaas_subscription_id ? 'Ativa' : 'Pendente' }}</v-chip>
          </div>
          <div class="account-row">
            <div>
              <strong>Última sincronização financeira</strong>
              <p>{{ formatDateTime(subscription.last_reconciled_at) }}</p>
            </div>
            <v-btn small outlined color="primary">Sincronizar</v-btn>
          </div>
        </section>

        <section v-show="activeSection === 'cartao'" id="cartao" class="account-section">
          <div class="account-section-title">
            <h2>Cartão de crédito</h2>
            <span>Forma de pagamento da mensalidade</span>
          </div>
          <div class="payment-method-box">
            <div class="payment-method-info">
              <v-icon>mdi-credit-card-outline</v-icon>
              <div>
                <strong>{{ paymentMethodTitle }}</strong>
                <p>{{ paymentMethodDescription }}</p>
              </div>
            </div>
            <v-btn outlined color="primary" @click="openCardDialog()">
              {{ paymentMethod ? 'Alterar cartão' : 'Configurar cartão' }}
            </v-btn>
          </div>
        </section>

        <section v-show="activeSection === 'transacoes'" id="transacoes" class="account-section">
          <div class="account-section-title">
            <h2>Transações</h2>
            <span>Histórico financeiro da conta</span>
          </div>
          <v-simple-table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Descrição</th>
                <th>Valor</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="transaction in transactions" :key="transaction.id">
                <td>{{ transaction.date }}</td>
                <td>{{ transaction.description }}</td>
                <td>{{ transaction.amount }}</td>
                <td><v-chip x-small :color="transaction.color" text-color="white">{{ transaction.status }}</v-chip></td>
              </tr>
            </tbody>
          </v-simple-table>
        </section>

        <section v-show="activeSection === 'avisos'" id="avisos" class="account-section">
          <div class="account-section-title">
            <h2>Avisos de cobrança</h2>
            <span>Notificações para evitar bloqueio por inadimplência</span>
          </div>
          <div class="notice-list">
            <label>
              <input v-model="billingNotices.before_due" type="checkbox">
              Avisar antes do vencimento
            </label>
            <label>
              <input v-model="billingNotices.payment_failed" type="checkbox">
              Avisar quando uma cobrança falhar
            </label>
            <label>
              <input v-model="billingNotices.suspension_risk" type="checkbox">
              Avisar antes de suspender o acesso
            </label>
          </div>
          <v-btn color="primary" disabled>Salvar avisos</v-btn>
        </section>

        <section v-show="activeSection === 'uso'" id="uso" class="account-section">
          <div class="account-section-title">
            <h2>Créditos</h2>
            <span>Consumo de gerações e validações no período atual</span>
          </div>
          <div class="usage-grid">
            <article>
              <span>Empresa</span>
              <strong>{{ quota.tenant_used || 0 }} / {{ quota.tenant_limit || 0 }}</strong>
              <v-progress-linear :value="tenantUsagePercent" height="8" rounded color="primary" />
            </article>
            <article>
              <span>Seu usuário</span>
              <strong>{{ quota.user_used || 0 }} / {{ quota.user_limit || 0 }}</strong>
              <v-progress-linear :value="userUsagePercent" height="8" rounded color="primary" />
            </article>
          </div>
        </section>
      </main>
    </div>

    <v-dialog v-model="cardDialog" max-width="720">
      <v-card>
        <v-card-title>Configurar cartão</v-card-title>
        <v-card-text class="card-payment-form">
          <v-alert type="info" dense text>
            O cartão será enviado para processamento seguro da assinatura. A plataforma não armazena número completo nem CVV.
          </v-alert>
          <v-alert v-if="cardError" type="error" dense text>
            {{ cardError }}
          </v-alert>
          <v-alert v-if="cardSuccess" type="success" dense text>
            {{ cardSuccess }}
          </v-alert>
          <div class="card-form-grid">
            <v-text-field v-model="cardForm.holder_name" label="Nome impresso no cartão" outlined dense hide-details />
            <v-select v-model="cardForm.plan_slug" :items="paidPlanOptions" label="Plano" outlined dense hide-details />
          </div>
          <div class="card-form-grid">
            <v-text-field
              v-model="cardForm.number"
              label="Número do cartão"
              outlined
              dense
              hide-details
              maxlength="23"
              inputmode="numeric"
              @input="cardForm.number = formatCardNumber($event)"
              @keypress="allowOnlyDigits"
            />
            <v-text-field
              v-model="cardForm.ccv"
              label="CVV"
              outlined
              dense
              hide-details
              maxlength="4"
              inputmode="numeric"
              type="password"
              @input="cardForm.ccv = onlyDigits($event, 4)"
              @keypress="allowOnlyDigits"
            />
          </div>
          <div class="card-form-grid">
            <v-select v-model="cardForm.exp_month" :items="monthOptions" label="Mês de validade" outlined dense hide-details />
            <v-select v-model="cardForm.exp_year" :items="yearOptions" label="Ano de validade" outlined dense hide-details />
          </div>
          <div class="card-form-grid">
            <v-text-field v-model="cardForm.billing_email" label="E-mail de cobrança" outlined dense hide-details />
            <v-text-field
              v-model="cardForm.cpf_cnpj"
              label="CPF/CNPJ do titular"
              outlined
              dense
              hide-details
              maxlength="18"
              @input="cardForm.cpf_cnpj = formatCpfCnpj($event)"
            />
          </div>
          <div class="card-form-grid">
            <v-text-field v-model="cardForm.postal_code" label="CEP do titular" outlined dense hide-details />
            <v-text-field v-model="cardForm.address_number" label="Número do endereço" outlined dense hide-details />
          </div>
          <div class="card-form-grid">
            <v-text-field
              v-model="cardForm.phone"
              label="Telefone"
              outlined
              dense
              hide-details
              maxlength="14"
              @input="cardForm.phone = formatPhone($event)"
            />
            <v-text-field
              v-model="cardForm.mobile_phone"
              label="Celular"
              outlined
              dense
              hide-details
              maxlength="15"
              @input="cardForm.mobile_phone = formatMobilePhone($event)"
            />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cardDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="savingCard" @click="savePaymentMethod">Salvar cartão</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import api from '../../plugins/axios'

export default {
  name: 'BillingPage',
  data () {
    return {
      subscription: {},
      quota: {},
      paymentMethod: null,
      loading: false,
      savingCard: false,
      error: '',
      success: '',
      cardError: '',
      cardSuccess: '',
      activeSection: 'geral',
      cardDialog: false,
      accountForm: {
        office_name: '',
        primary_email: ''
      },
      originalAccountForm: {
        office_name: '',
        primary_email: ''
      },
      cardForm: {
        plan_slug: 'starter',
        holder_name: '',
        number: '',
        ccv: '',
        exp_month: '',
        exp_year: '',
        billing_email: '',
        cpf_cnpj: '',
        postal_code: '',
        address_number: '',
        phone: '',
        mobile_phone: ''
      },
      billingNotices: {
        before_due: true,
        payment_failed: true,
        suspension_risk: true
      },
      sections: [
        { id: 'geral', label: 'Geral', icon: 'mdi-cog-outline' },
        { id: 'plano', label: 'Plano', icon: 'mdi-package-variant-closed' },
        { id: 'cobranca', label: 'Cobrança', icon: 'mdi-receipt-text-outline' },
        { id: 'cartao', label: 'Cartão', icon: 'mdi-credit-card-outline' },
        { id: 'transacoes', label: 'Transações', icon: 'mdi-format-list-bulleted' },
        { id: 'avisos', label: 'Avisos', icon: 'mdi-bell-outline' },
        { id: 'uso', label: 'Créditos', icon: 'mdi-chart-line' }
      ],
      plans: [
        { name: 'Teste', slug: 'test', monthly_price: 5, max_users: 1, max_ai_validations: 20 },
        { name: 'Starter', slug: 'starter', monthly_price: 59.9, max_users: 1, max_ai_validations: 100 },
        { name: 'Professional', slug: 'professional', monthly_price: 159.9, max_users: 1, max_ai_validations: 500 },
        { name: 'Business', slug: 'business', monthly_price: 349.9, max_users: 1, max_ai_validations: 2500 },
        { name: 'Starter Corporativo', slug: 'starter_plus', monthly_price: 149.9, max_users: 3, max_ai_validations: 300 },
        { name: 'Professional Corporativo', slug: 'professional_plus', monthly_price: 349.9, max_users: 10, max_ai_validations: 1000 },
        { name: 'Business Corporativo', slug: 'business_plus', monthly_price: 699.9, max_users: 25, max_ai_validations: 5000 }
      ]
    }
  },
  computed: {
    planName () {
      return this.subscription.plan ? this.subscription.plan.name : 'Trial'
    },
    currentPlanSlug () {
      return this.subscription.plan ? this.subscription.plan.slug : 'trial'
    },
    tenantUsagePercent () {
      return this.percent(this.quota.tenant_used, this.quota.tenant_limit)
    },
    userUsagePercent () {
      return this.percent(this.quota.user_used, this.quota.user_limit)
    },
    accountFormChanged () {
      return this.normalizeText(this.accountForm.office_name) !== this.normalizeText(this.originalAccountForm.office_name) ||
        this.normalizeText(this.accountForm.primary_email) !== this.normalizeText(this.originalAccountForm.primary_email)
    },
    monthOptions () {
      return Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, '0'))
    },
    yearOptions () {
      const current = new Date().getFullYear()
      return Array.from({ length: 12 }, (_, index) => String(current + index))
    },
    paidPlanOptions () {
      return this.plans.map(plan => ({ text: `${plan.name} - ${this.money(plan.monthly_price)}/mês`, value: plan.slug }))
    },
    paymentMethodTitle () {
      return this.paymentMethod
        ? `${this.paymentMethod.brand || 'Cartão'} final ${this.paymentMethod.last4}`
        : 'Cartão não configurado'
    },
    paymentMethodDescription () {
      if (!this.paymentMethod) {
        return 'Ao selecionar um plano pago, o cartão será cadastrado em ambiente seguro de pagamento.'
      }

      return `Validade ${this.paymentMethod.exp_month}/${this.paymentMethod.exp_year}. Cobranças mensais usam este método de pagamento.`
    },
    transactions () {
      const amount = this.money(this.subscription.plan ? this.subscription.plan.monthly_price : 0)
      const rows = [
        {
          id: 'current-period',
          date: this.formatDate(this.subscription.current_period_start || this.subscription.created_at),
          description: this.subscription.status === 'trialing' ? 'Trial ComplyOps iniciado' : `Mensalidade ${this.planName}`,
          amount,
          status: this.statusLabel(this.subscription.status),
          color: this.statusColor(this.subscription.status)
        }
      ]

      if (this.subscription.last_asaas_event) {
        rows.push({
          id: 'last-event',
          date: this.formatDate(this.subscription.updated_at),
          description: 'Atualização financeira sincronizada',
          amount,
          status: 'Sincronizado',
          color: 'primary'
        })
      }

      return rows
    }
  },
  created () {
    this.load()
  },
  methods: {
    async load () {
      this.loading = true
      this.error = ''
      try {
        const { data } = await api.get('/billing/subscription')
        this.subscription = data.subscription || data || {}
        this.quota = data.ai_validation_quota || {}
        this.paymentMethod = data.payment_method || null
        this.accountForm.office_name = this.$store.state.auth.tenant ? this.$store.state.auth.tenant.name : ''
        this.accountForm.primary_email = this.$store.state.auth.user ? this.$store.state.auth.user.email : ''
        this.originalAccountForm = { ...this.accountForm }
      } catch (error) {
        this.error = 'Não foi possível carregar os dados da conta.'
      } finally {
        this.loading = false
      }
    },
    selectPlan (planSlug) {
      this.openCardDialog(planSlug)
    },
    openCardDialog (planSlug) {
      this.cardError = ''
      this.cardSuccess = ''
      const selectedPlanSlug = planSlug || (this.currentPlanSlug === 'trial' ? 'starter' : this.currentPlanSlug)
      this.cardForm = this.paymentMethod
        ? {
            plan_slug: selectedPlanSlug,
            holder_name: this.paymentMethod.holder_name || '',
            number: '',
            ccv: '',
            exp_month: this.paymentMethod.exp_month || '',
            exp_year: this.paymentMethod.exp_year || '',
            billing_email: this.paymentMethod.billing_email || this.accountForm.primary_email || '',
            cpf_cnpj: '',
            postal_code: '',
            address_number: '',
            phone: '',
            mobile_phone: ''
          }
        : {
            plan_slug: selectedPlanSlug,
            holder_name: '',
            number: '',
            ccv: '',
            exp_month: '',
            exp_year: '',
            billing_email: this.accountForm.primary_email || '',
            cpf_cnpj: '',
            postal_code: '',
            address_number: '',
            phone: '',
            mobile_phone: ''
          }
      this.cardDialog = true
    },
    async savePaymentMethod () {
      this.savingCard = true
      this.cardError = ''
      this.cardSuccess = ''
      try {
        const payload = {
          ...this.cardForm,
          number: String(this.cardForm.number || '').replace(/\D/g, ''),
          ccv: String(this.cardForm.ccv || '').replace(/\D/g, ''),
          cpf_cnpj: String(this.cardForm.cpf_cnpj || '').replace(/\D/g, ''),
          postal_code: String(this.cardForm.postal_code || '').replace(/\D/g, ''),
          phone: String(this.cardForm.phone || '').replace(/\D/g, ''),
          mobile_phone: String(this.cardForm.mobile_phone || '').replace(/\D/g, '')
        }
        const { data } = await api.post('/billing/payment-method', payload)
        this.paymentMethod = data
        await this.load()
        this.cardSuccess = 'Cartão configurado com sucesso.'
        window.setTimeout(() => {
          this.cardDialog = false
          this.success = this.subscription.status === 'active'
            ? 'Cartão configurado e assinatura ativa.'
            : 'Cartão configurado. Aguardando confirmação do pagamento.'
        }, 600)
      } catch (error) {
        this.cardError = error.response && error.response.data ? error.response.data.message : 'Não foi possível configurar o cartão.'
      } finally {
        this.savingCard = false
      }
    },
    statusLabel (status) {
      return { trialing: 'Trial', pending_payment: 'Aguardando pagamento', active: 'Ativa', past_due: 'Em atraso', overdue: 'Inadimplente', payment_failed: 'Pagamento recusado', suspended: 'Suspensa', cancelled: 'Cancelada' }[status] || status || '-'
    },
    statusColor (status) {
      return { trialing: 'primary', pending_payment: 'warning', active: 'success', past_due: 'warning', overdue: 'error', payment_failed: 'error', suspended: 'grey', cancelled: 'grey' }[status] || 'grey'
    },
    percent (used, limit) {
      const total = Number(limit || 0)
      if (!total) return 0
      return Math.min((Number(used || 0) / total) * 100, 100)
    },
    normalizeText (value) {
      return String(value || '').trim()
    },
    formatCpfCnpj (value) {
      const digits = String(value || '').replace(/\D/g, '').slice(0, 14)

      if (digits.length <= 11) {
        return digits
          .replace(/^(\d{3})(\d)/, '$1.$2')
          .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
          .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4')
      }

      return digits
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
        .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, '$1.$2.$3/$4-$5')
    },
    formatCardNumber (value) {
      return this.onlyDigits(value, 19).replace(/(\d{4})(?=\d)/g, '$1 ')
    },
    onlyDigits (value, maxLength) {
      return String(value || '').replace(/\D/g, '').slice(0, maxLength)
    },
    allowOnlyDigits (event) {
      if (!/^\d$/.test(event.key)) {
        event.preventDefault()
      }
    },
    formatPhone (value) {
      const digits = String(value || '').replace(/\D/g, '').slice(0, 10)

      return digits
        .replace(/^(\d{2})(\d)/, '($1) $2')
        .replace(/^(\(\d{2}\) \d{4})(\d)/, '$1-$2')
    },
    formatMobilePhone (value) {
      const digits = String(value || '').replace(/\D/g, '').slice(0, 11)

      if (digits.length <= 10) {
        return this.formatPhone(digits)
      }

      return digits
        .replace(/^(\d{2})(\d)/, '($1) $2')
        .replace(/^(\(\d{2}\) \d{5})(\d)/, '$1-$2')
    },
    money (value) {
      return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    formatDate (value) {
      return value ? new Date(value).toLocaleDateString('pt-BR') : '-'
    },
    formatDateTime (value) {
      return value ? new Date(value).toLocaleString('pt-BR') : 'Ainda não executada'
    }
  }
}
</script>
